import {
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { IssueOccurrencesTable } from "../components/issue-occurrences-table.tsx";
import { RunStatusChip } from "../components/run-status-chip.tsx";
import { useApiMutation } from "../hooks/use-api-mutation.ts";
import { useApiQuery } from "../hooks/use-api-query.ts";
import { usePaginatedApiQuery } from "../hooks/use-paginated-api-query.ts";
import { useTypedQueryParams } from "../hooks/use-query-params.ts";
import type { IssueOccurrence, TestRun, TestSuite, TestSuiteRun } from "../types.ts";
import { formatDurationFromTimes } from "../utils";
import { humanizeText } from "../utils/text.ts";
import TestRunsPage from "./runs.tsx";

interface SuiteRunDetailsTabs {
  tab?: "test-runs" | "issues";
}

export default function SuiteRunDetailsPage() {
  const { suiteRunId } = useParams();
  const { params, setParam } = useTypedQueryParams<SuiteRunDetailsTabs>();
  const activeTab = params.tab ?? "test-runs";

  const { enqueueSnackbar } = useSnackbar();
  const nav = useNavigate();

  const { data, isLoading, refetch } = useApiQuery<TestSuiteRun>(`/suite-runs/${suiteRunId}`, {
    enabled: !!suiteRunId,
    refetchInterval: 3000, // Refetch every 3 seconds to update progress
  });

  // Fetch the test suite to get expected test IDs
  const { data: testSuite } = useApiQuery<TestSuite>(`/test-suites/${data?.test_suite_id}`, {
    enabled: !!data?.test_suite_id,
  });

  // Fetch test runs for this suite run
  const { data: testRuns } = useApiQuery<TestRun[]>(`/runs?suite_run_id=${suiteRunId}`, {
    enabled: !!suiteRunId,
    refetchInterval: 3000, // Refetch every 3 seconds to update progress
  });

  const { mutate: cancel, isPending: isCancelling } = useApiMutation<void>(
    "POST",
    `/suite-runs/${suiteRunId}/cancel`,
    {
      onSuccess: () => {
        enqueueSnackbar("Test Suite Run cancelled", {
          variant: "warning",
          // For the AI agent to be able to read the message
          autoHideDuration: 10_000,
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });

        refetch();
      },
    },
  );

  const {
    data: issuesData,
    isLoading: isLoadingIssues,
    setFilters,
  } = usePaginatedApiQuery<IssueOccurrence>(
    `/issue-occurrences`,
    { page: 0, pageSize: 10 },
    {},
    undefined,
    {
      enabled: !!suiteRunId && activeTab === "issues",
    },
  );

  useEffect(() => {
    if (!suiteRunId) return;
    setFilters({ test_suite_run_id: suiteRunId });
  }, [suiteRunId, setFilters]);

  const duration = useMemo(() => {
    return formatDurationFromTimes(data?.start_time, data?.end_time, {
      showRunning: true,
    });
  }, [data?.start_time, data?.end_time]);

  // Calculate test run totals based on run status
  const testTotals = useMemo(() => {
    if (!testSuite || !testRuns) {
      return null;
    }

    // Get expected test IDs from the test suite
    const expectedTestIds = new Set(testSuite.test_ids);
    const totalTests = expectedTestIds.size;

    // Only consider original runs (not reruns)
    const originalRuns = testRuns.filter((run) => !run.rerun_id);

    // Get unique test IDs that have completed (not pending or running)
    const completedTestIds = new Set(
      originalRuns
        .filter((run) => {
          const status = run.final_run_status || run.run_status;
          return !["pending", "running"].includes(status);
        })
        .map((run) => run.test_id),
    );
    const completedTests = completedTestIds.size;

    // Get unique test IDs that are running (for buffer)
    const runningTestIds = new Set(
      originalRuns
        .filter((run) => {
          const status = run.final_run_status || run.run_status;
          return status === "running";
        })
        .map((run) => run.test_id),
    );
    const runningTests = runningTestIds.size;

    // Calculate percentage and buffer
    const percentage = totalTests > 0 ? (completedTests / totalTests) * 100 : 0;
    const bufferPercentage =
      totalTests > 0 ? ((completedTests + runningTests) / totalTests) * 100 : 0;

    return {
      total: totalTests,
      completed: completedTests,
      percentage: Math.round(percentage),
      bufferPercentage: Math.round(bufferPercentage),
    };
  }, [testSuite, testRuns]);

  if (isLoading) {
    return <CircularProgress />;
  }

  if (!data) {
    return <Typography>Run not found</Typography>;
  }

  const status = data.run_status;
  const finalStatus = data.final_run_status;

  let label = humanizeText(status) ?? "Unknown";
  let tooltip = "";

  if (finalStatus) {
    label = `${humanizeText(finalStatus)} *`;
    tooltip = `The original status was ${humanizeText(status) ?? "Unknown"}`;
  }

  const handleViewIssue = (occurrence: IssueOccurrence) => {
    nav(`/issues/${occurrence.issue_id}`);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "test-runs":
        return <TestRunsPage suiteId={data.test_suite_id} suiteRunId={data.id} noHeader />;
      case "issues":
        if (isLoadingIssues) {
          return <CircularProgress />;
        }

        if (!issuesData || issuesData.length === 0) {
          return (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              py={6}
              sx={{ textAlign: "center" }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                🎉 No issues found!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your test suite run completed without any issues. Great job!
              </Typography>
            </Box>
          );
        }

        return (
          <IssueOccurrencesTable
            data={issuesData}
            isLoading={isLoadingIssues}
            onView={handleViewIssue}
            height={600}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={0} alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="column" spacing={1} alignItems="flex-start">
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={tooltip}>
              <span>
                <RunStatusChip status={finalStatus ?? status} label={label} />
              </span>
            </Tooltip>
            <Typography variant="h5">Test Suite Run Results</Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            alignItems="flex-start"
            divider={<Typography variant="body2">·</Typography>}
          >
            <Tooltip title="Start time">
              <Typography variant="body2">
                {new Date(data.start_time ?? data.created_at).toLocaleString()}
              </Typography>
            </Tooltip>
            {data.end_time ? (
              <Tooltip title="End time">
                <Typography variant="body2">{new Date(data.end_time).toLocaleString()}</Typography>
              </Tooltip>
            ) : (
              <Box className="loader" />
            )}
            {duration ? (
              <Typography variant="body2">{duration}</Typography>
            ) : (
              <Box className="loader" />
            )}
          </Stack>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          {testTotals && (
            <Box sx={{ minWidth: 240, maxWidth: 350, width: "100%", flex: 1 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {testTotals.completed} / {testTotals.total} tests
                </Typography>
                <Chip
                  label={`${testTotals.percentage}%`}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    flexShrink: 0,
                    backgroundColor: "info.main",
                    color: "common.white",
                  }}
                />
              </Stack>
              <LinearProgress
                variant="buffer"
                value={testTotals.percentage}
                valueBuffer={testTotals.bufferPercentage}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "grey.300",
                  "& .MuiLinearProgress-bar1": {
                    borderRadius: 3,
                    backgroundColor: "info.main",
                  },
                  "& .MuiLinearProgress-bar2": {
                    borderRadius: 3,
                    backgroundColor: "info.light",
                  },
                  "& .MuiLinearProgress-dashed": {
                    display: "none",
                  },
                }}
              />
            </Box>
          )}
          <Tooltip title="Cancel Test Suite Run">
            <Button
              variant="outlined"
              color="warning"
              disabled={
                !data.external_id ||
                !["running", "pending"].includes(data.final_run_status ?? data.run_status) ||
                isCancelling
              }
              loading={isCancelling}
              onClick={() => {
                cancel({});
              }}
              sx={{ minWidth: "100px" }}
            >
              Cancel
            </Button>
          </Tooltip>
          <Tooltip title="Go to Test Suite">
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                nav(`/tests-main/test-suites/${data.test_suite_id}`);
              }}
              sx={{ minWidth: "150px" }}
            >
              Go to Suite
            </Button>
          </Tooltip>
        </Stack>
      </Stack>

      <Box mt={2}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setParam("tab", newValue)}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Test Runs" value="test-runs" />
          <Tab label="Issues" value="issues" />
        </Tabs>

        <Box mt={2}>{renderTabContent()}</Box>
      </Box>
    </Box>
  );
}
